import React, { useState } from 'react';
import { useTranslationStore } from '../store/translationStore';
import { ClockIcon, TrashIcon } from '@heroicons/react/24/outline';

// Import HistoryItem type from the store
interface HistoryItem {
  id: string;
  original: string;
  translation: string;
  officialTranslation: string;
  alternatives?: Array<{
    term: string;
    pinyin?: string;
    context: string;
  }>;
  pinyin?: string;
  examples: string[];
  category?: string;
  sourceLang: string;
  targetLang: string;
  searchCount: number;
  lastSearched: Date;
}

const HistoryList: React.FC = () => {
  const { history, deleteHistoryItem, loadHistoryItem } = useTranslationStore();
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const handleDelete = async (id: string) => {
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await deleteHistoryItem(id);
    } catch (error) {
      console.error('Failed to delete item:', error);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleHistoryItemClick = async (item: HistoryItem) => {
    setLoadingIds(prev => new Set(prev).add(item.id));
    try {
      await loadHistoryItem(item);
    } catch (error) {
      console.error('Failed to load history item:', error);
    } finally {
      setLoadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  if (history.length === 0) {
    return (
      <div className="text-center py-8">
        <ClockIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p className="text-gray-500">No search history yet</p>
      </div>
    );
  }

  // Sort history by search count (descending), then by recency (most recent first)
  const sortedHistory = [...history].sort((a, b) => {
    if (a.searchCount !== b.searchCount) {
      return b.searchCount - a.searchCount; // Higher search count first
    }
    return new Date(b.lastSearched).getTime() - new Date(a.lastSearched).getTime(); // More recent first
  });

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Top Searches</h2>

      <div className="space-y-4">
        {sortedHistory.map((item) => {
          const isLoading = loadingIds.has(item.id);
          return (
            <div
              key={item.id}
              className={`py-2 border-b border-gray-100 hover:bg-gray-50 hover:shadow-sm transition-all duration-200 cursor-pointer rounded-lg px-2 -mx-2 ${
                isLoading ? 'opacity-60 pointer-events-none' : ''
              }`}
              onClick={() => handleHistoryItemClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHistoryItemClick(item);
                }
              }}
              title={isLoading ? "Loading..." : "Click to load this translation"}
            >
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-baseline">
                <div className="flex items-baseline space-x-2 flex-1 min-w-0 mr-2">
                  <p className="text-base font-medium text-gray-900 whitespace-nowrap">{item.translation}</p>
                  <p className="text-sm text-gray-500 whitespace-nowrap">({item.original})</p>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <div className="flex items-center text-sm text-gray-500">
                    <span>x{item.searchCount}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    disabled={deletingIds.has(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Delete this translation"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center">
                {item.pinyin && (
                  <p className="text-sm text-gray-400">{item.pinyin}</p>
                )}
                {!item.pinyin && <div />}
                {item.category && (
                  <span className="inline-block px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                    {item.category}
                  </span>
                )}
              </div>
            </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HistoryList;